import { KeyboardEvent } from 'react'

/**
 * Prevents invalid characters in whole number input fields.
 * Blocks decimal points, commas, scientific notation, and sign characters.
 * Use this with JTextField's onKeyDown prop for percentage/whole number fields.
 *
 * Blocked characters:
 * - `.` and `,` - Decimal separators
 * - `e` - Scientific notation (e.g., "1e2")
 * - `+` and `-` - Sign characters
 *
 * @example
 * <JTextField
 *   type="number"
 *   onKeyDown={blockDecimalInput}
 * />
 */
export const blockDecimalInput = (e: KeyboardEvent<HTMLInputElement>) => {
  if (['.', ',', 'e', '+', '-'].includes(e.key)) {
    e.preventDefault()
  }
}

/**
 * Validates that a value is an integer.
 * Use this with react-hook-form's validate rule.
 *
 * @param value - The value to validate
 * @returns Error message string if validation fails, true if validation passes
 *
 * @example
 * <JTextField
 *   rules={{
 *     validate: validateIsInteger,
 *     min: { value: 0, message: 'Fee must be at least 0.' },
 *     max: { value: 100, message: 'Fee cannot exceed 100.' }
 *   }}
 * />
 *
 * @note Empty strings or null values are treated as 0 and will pass validation.
 * Use the 'required' rule to disallow empty values.
 */
export const validateIsInteger = (
  value: string | number | boolean
): string | true => {
  const numValue = Number(value)
  if (!Number.isInteger(numValue)) {
    return 'Fee must be a whole number'
  }
  return true
}

/**
 * Normalizes a US phone value to bare 10 digits (ENG-1468).
 *
 * Browser/password-manager autofill bypasses the react-imask mask and can land
 * values like "+1 555 123 4567", "(555) 123-4567" or "15551234567" in form
 * state — which then fail the strict 10-digit validation (or worse, get
 * partially consumed by the mask into a wrong number). Strips every non-digit
 * and drops a single leading US country code ("1") when exactly 11 digits
 * remain.
 *
 * Non-US formats are out of scope — anything that doesn't reduce to 10 digits
 * is returned as its bare-digits form and left for validation to reject.
 */
export const normalizeUsPhone = (value: unknown): string => {
  const digits = String(value ?? '').replace(/\D/g, '')
  return digits.replace(/^1(?=\d{10}$)/, '')
}

/**
 * react-hook-form `validate` rule for a US phone (ENG-1828).
 *
 * Normalizes autofill/paste shapes with {@link normalizeUsPhone} BEFORE the
 * strict 10-digit check, so a value like "(555) 123-4567" or "+1 555 123 4567"
 * passes instead of being blocked. This is the same normalization ENG-1468
 * applied to the InitialStep/PersonalInformation yup schemas, adapted to a
 * field that uses native RHF rules.
 *
 * Empty passes here — pair with the `required` rule to disallow blanks (same
 * contract as {@link validateIsInteger}).
 */
export const validateUsPhone = (value: unknown): string | true => {
  const normalized = normalizeUsPhone(value)
  if (!normalized) return true
  return /^\d{10}$/.test(normalized) || 'Must be a 10-digit phone number'
}

/**
 * Consumer email providers treated as "personal" at signup (ENG-1602, v1 list).
 *
 * A firm-domain address is a weak identity signal for free; a personal one is
 * none. Signup is never blocked on this — a match only surfaces a warning and
 * routes the account to manual verification.
 */
export const PERSONAL_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'ymail.com',
]

/**
 * True when the address's domain is a known consumer email provider.
 *
 * Matches on everything after the last "@", trimmed and lowercased, so
 * " Person@Gmail.COM" counts. A value with no "@" (blank, or still being
 * typed) never matches — email format itself is validated separately, and
 * subdomains like "@mail.gmail.com" are intentionally not matched.
 */
export const isPersonalEmailDomain = (value: unknown): boolean => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
  const atIndex = normalized.lastIndexOf('@')

  if (atIndex === -1) {
    return false
  }

  return PERSONAL_EMAIL_DOMAINS.includes(normalized.slice(atIndex + 1))
}
